import React, {useEffect, useMemo, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/pl';
import {FaArrowLeft, FaFileAlt, FaFolder, FaHistory} from 'react-icons/fa';
import {clearRecentFiles, getRecentFiles} from '../../../infrastructure/storage/localStorage';
import {useSelectSong} from '../../navigation/components/Navigation';
import {buildCatalog, catalogUrl, fileActivity, fileDirectory, fileTitle, findCatalog, isSongbook} from './dashboardData';
import {songPathFromUrl} from '../../../shared/services/songPathUtils';
import {useSongbookLibrary} from './useSongbookLibrary';
import './DashboardComponent.css';

const recentEntries = () => getRecentFiles().filter(file => isSongbook(file.fileName));
moment.locale('pl');

export const RecentFilesPage = () => {
  const [entries, setEntries] = useState(recentEntries);
  const [clock, setClock] = useState(Date.now);
  const selectSong = useSelectSong();
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const groups = useMemo(() => {
    const grouped = new Map();
    entries.forEach(file => {
      const date = moment(file.dateTime);
      const label = date.isSame(moment(clock), 'day') ? 'Dzisiaj'
        : date.isSame(moment(clock).subtract(1, 'day'), 'day') ? 'Wczoraj'
        : date.fromNow();
      if (!grouped.has(label)) grouped.set(label, []);
      grouped.get(label).push(file);
    });
    return [...grouped.entries()];
  }, [entries, clock]);
  return <main className="dashboard-page dashboard-recent-page">
    <div className="dashboard-recent-heading"><h1><FaHistory aria-hidden="true"/> Ostatnio przeglądane</h1>
      <span className="dashboard-recent-count">{entries.length}<span> śpiewników</span></span></div>
    <div className="dashboard-history-list">
      {!!entries.length && <table aria-label="Ostatnio przeglądane śpiewniki"><thead><tr><th scope="col">Nazwa i katalog</th><th scope="col">Otwarto</th></tr></thead>
        {groups.map(([label, files]) => <tbody key={label}>
          <tr className="dashboard-history-group"><th colSpan="2" scope="rowgroup"><h2>{label} <small>· {files.length}</small></h2></th></tr>
          {files.map(file => <tr className="dashboard-history-row" key={file.fileName}>
            <td><button type="button" className="dashboard-history-file" aria-label={fileTitle(file.fileName)} onClick={() => selectSong(file.fileName, file.songNumber)}>
              <FaFileAlt className="dashboard-history-file-icon" aria-hidden="true"/>
              <span className="dashboard-history-file-details"><span className="dashboard-history-file-title" title={fileTitle(file.fileName)}>{fileTitle(file.fileName)}</span>
                <span className="dashboard-history-directory"><FaFolder aria-hidden="true"/><span title={fileDirectory(file.fileName) || 'Główny'}>{fileDirectory(file.fileName) || 'Główny'}</span></span>
              </span></button></td>
            <td><time dateTime={file.dateTime} className={clock - moment(file.dateTime).valueOf() < 60_000 ? 'dashboard-history-time-new' : undefined}
              title={moment(file.dateTime).format('D MMMM YYYY, HH:mm')}>{moment(file.dateTime).from(moment(clock))}</time></td>
          </tr>)}
        </tbody>)}
      </table>}
      {!entries.length && <p className="dashboard-empty">Historia otwarć jest pusta.</p>}
    </div>
    <button type="button" className="dashboard-history-clear" onClick={() => {clearRecentFiles(); setEntries([]);}} disabled={!entries.length}>Wyczyść historię</button>
  </main>;
};

export const CatalogPage = () => {
  const {files, modifiedTimes} = useSongbookLibrary();
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const selectSong = useSelectSong();
  const openedFiles = new Map(recentEntries().map(file => [file.fileName, file]));
  const recent = files.map(file => fileActivity(file, openedFiles.get(file), modifiedTimes[file])).filter(Boolean)
    .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
  const root = useMemo(() => buildCatalog(files), [files]);
  const path = songPathFromUrl(location.pathname);
  const current = findCatalog(root, path);
  const go = target => navigate(catalogUrl(target));
  const up = () => {
    if (!path) navigate('/app/dashboard');
    else go(path.split('/').slice(0, -1).join('/'));
  };
  const folders = current?.folders || [];
  const directFiles = current?.files || [];
  const crumbs = path ? path.split('/') : [];
  return <main className="dashboard-page dashboard-catalog-page">
    <div className="dashboard-page-head"><div><h1><FaFolder/> Katalogi śpiewników</h1>
      <nav className="dashboard-breadcrumb" aria-label="Ścieżka katalogu">
        <button type="button" onClick={up} aria-label="Poziom wyżej"><FaArrowLeft/></button>
        <button type="button" onClick={() => go('')}>Katalogi</button>
        {crumbs.map((part, index) => <React.Fragment key={index}><span>/</span><button type="button" onClick={() => go(crumbs.slice(0, index + 1).join('/'))}>{part}</button></React.Fragment>)}
      </nav></div><span>{current?.allFiles.length || 0} śpiewników</span></div>
    {!current && <p className="dashboard-empty">Ten katalog już nie istnieje. <Link to={catalogUrl('')}>Przejdź do katalogów</Link></p>}
    {current && <>
      {directFiles.length > 0 && <section className="dashboard-directory-files"><h2>Śpiewniki w tym katalogu</h2>
        {directFiles.map(file => <button type="button" key={file.path} onClick={() => selectSong(file.path)}>
          <FaFileAlt/> {fileTitle(file.path)}
        </button>)}</section>}
      <div className="dashboard-catalog-grid">
        {folders.map(folder => {
          const latest = recent.filter(file => folder.allFiles.includes(file.fileName)).slice(0, 2);
          return <article key={folder.path} className={'dashboard-catalog-card' + (latest.length ? ' dashboard-catalog-featured' : '')}
            onClick={event => { if (!event.target.closest('a, button')) go(folder.path); }}>
            <Link className="dashboard-catalog-open" to={catalogUrl(folder.path)} aria-label={`Otwórz katalog ${folder.name}`} title={folder.path}>
              <span className="dashboard-catalog-card-title"><FaFolder aria-hidden="true"/><span className="dashboard-catalog-card-name">{folder.name}</span></span>
              <span className="dashboard-catalog-card-count" aria-label={`${folder.allFiles.length} śpiewników`}>{folder.allFiles.length}<span className="dashboard-catalog-count-label"> śpiewników</span></span>
            </Link>
            {latest.length > 0 && <div className="dashboard-catalog-latest">
              <span>Ostatnio:</span>
              {latest.map(file => <button type="button" className="dashboard-catalog-recent-file" key={file.fileName}
                title={file.fileName} aria-label={fileTitle(file.fileName)} onClick={() => selectSong(file.fileName, file.songNumber)}>
                <FaFileAlt aria-hidden="true"/><span>{fileTitle(file.fileName)}</span>
                <time className="dashboard-catalog-recent-time" dateTime={file.dateTime} title={moment(file.dateTime).format('D MMMM YYYY, HH:mm')}>
                  {file.kind === 'opened' ? 'Otwarto' : 'Zmodyfikowano'} {moment(file.dateTime).from(moment(clock))}
                </time>
              </button>)}
            </div>}
            {latest.length > 0 && <Link className="dashboard-catalog-more" to={catalogUrl(folder.path)} aria-label={`Pełna lista: ${folder.name}`}>Pełna lista →</Link>}
          </article>;
        })}
      </div>
      {!folders.length && !directFiles.length && <p className="dashboard-empty">Ten katalog jest pusty.</p>}
    </>}
  </main>;
};
